// @ts-nocheck
import { isNull, isUndefined } from "lodash";
import {
  convertFromUTCToGivenTimezone,
  formatAMPM,
} from "../utils/methods/dateAndTimeMethods";
import { TRAVEL_DAYS } from "./calcConstants";
import { getUTCTimeDifferenceWithDate } from "./calcTime";
import { DateTime } from "luxon";

export function getBreaks(
  pos,
  discardSecondBreak = false,
  isContractor = false,
  isPayroll = false,
) {
  const isWalkAway = pos?.selectOption?.toLowerCase() === "1 hour walk away";
  if (!isPayroll && !isWalkAway) return null;
  const pairs = isContractor
    ? [
        [pos?.contractorFirstBreak, pos?.contractorAfterFirstBreakIn],
        [pos?.contractorSecondBreak, pos?.contractorAfterSecondBreakIn],
      ]
    : [
        [pos?.firstBreak, pos?.afterFirstBreakIn],
        [pos?.secondBreak, pos?.afterSecondBreakIn],
      ];
  const hasActualData = isContractor
    ? !!(pos?.contractorActualStart || pos?.contractorActualEnd)
    : !!(pos?.actualStart || pos?.actualEnd);
  // Preserve the planned TBD allowance only until times or breaks are recorded.
  const hasRecordedBreak = pairs.some(([start, end]) => start || end);
  if (isTimeTBD(pos?.timeIn) && isTimeTBD(pos?.timeOut) && !hasActualData &&
      (!isPayroll || !hasRecordedBreak) && isWalkAway) {
    if (pos?.numberOfHours > 5) {
      return {
        firstBreak: isPayroll && pos?.firstMealPaid === true ? 0 : 1,
        secondBreak: 0,
      };
    }
  }

  const duration = ([start, end], paid) => {
    if (!start || !end) return 0;
    if (!isPayroll) return getUTCTimeDifferenceWithDate(start, end);
    if (paid === true) return 0;
    // Payroll uses each recorded meal, regardless of the planning option.
    // Compare before rounding: a 59m59s meal must remain paid.
    let milliseconds = Date.parse(end) - Date.parse(start);
    if (milliseconds < 0) milliseconds += 24 * 60 * 60 * 1000;
    if (!Number.isFinite(milliseconds) || milliseconds < 60 * 60 * 1000) return 0;
    return Number((milliseconds / (60 * 60 * 1000)).toFixed(2));
  };
  return {
    firstBreak: duration(pairs[0], pos?.firstMealPaid),
    secondBreak: discardSecondBreak ? 0 : duration(pairs[1], pos?.secondMealPaid),
  };
}

export function getBreaksEstimate(pos, discardSecondBreak = false) {
  if (pos?.selectOption?.toLowerCase() !== "1 hour walk away") return null;
  if (isTimeTBD(pos?.timeIn) && isTimeTBD(pos?.timeOut)) {
    if (pos?.numberOfHours > 5) {
      return {
        firstBreak: 1,
        secondBreak: 0,
      };
    }
  }

  return {
    firstBreak:
      pos?.firstBreak && pos?.afterFirstBreakIn
        ? getUTCTimeDifferenceWithDate(pos?.firstBreak, pos?.afterFirstBreakIn)
        : 0,
    secondBreak: discardSecondBreak
      ? 0
      : pos?.secondBreak && pos?.afterSecondBreakIn
        ? getUTCTimeDifferenceWithDate(
            pos?.secondBreak,
            pos?.afterSecondBreakIn,
          )
        : 0,
  };
}

export function isTimeTBD(str) {
  if (!str) return true;
  if (str?.toLowerCase() === "tbd") return true;
  else return false;
}

export const convertTime = ({ dateStr, timezone }) => {
  let returnVal = "";
  const utcDateTime = DateTime.fromISO(dateStr, { zone: "UTC" });

  // Convert the UTC time to the specified timezone
  const localDateTime = utcDateTime.setZone(timezone);

  // Return the local time in ISO format with the appropriate local timezone offset
  const splittedDate = localDateTime?.toISO()?.split("T");

  const time = splittedDate?.[1]?.split("-")?.[0];

  // returnVal = splittedDate?.[0] + "T" + time?.split("+")?.[0];
  returnVal = splittedDate?.[0] + "T" + time?.split("+")?.[0] + "Z";

  return returnVal;
};

export function getTimeDiff(utcDate1, utcDate2, terms) {
  try {
    if (!utcDate1 || !utcDate2 || !terms) {
      throw new Error("Missing required inputs");
    }

    const date1 = Date.parse(utcDate1);
    let date2 = Date.parse(utcDate2);

    if (isNaN(date1) || isNaN(date2)) {
      throw new Error("Invalid date format");
    }

    const dayInMilliseconds = 24 * 60 * 60 * 1000;
    if (date2 <= date1) {
      date2 += dayInMilliseconds;
      if (date2 <= date1) {
        date2 += dayInMilliseconds;
      }
    }

    let difference = date2 - date1;

    const absoluteDifference = Math.abs(difference);
    const hoursDifference = absoluteDifference / (1000 * 60 * 60);
    const diff = hoursDifference.toFixed(2);

    const fullDayMilliseconds = terms.fullDay * 60 * 60 * 1000;

    let midnightMilliseconds = date1 - date1 * dayInMilliseconds;

    if (date2 - date1 >= dayInMilliseconds) {
      midnightMilliseconds = date1 - (date1 % dayInMilliseconds);
    } else {
      midnightMilliseconds = date2 - (date2 % dayInMilliseconds);
    }
    const midnightTo8amStart = midnightMilliseconds;
    // NOTE: changed to midnight to 6am
    const midnightTo8amEnd = midnightMilliseconds + 6 * 60 * 60 * 1000;

    const fullTimeEnd = Math.min(date1 + fullDayMilliseconds, date2);
    // const fullTimeEnd = date1 + fullDayMilliseconds;

    let afterFullTime = false;
    let overtime = 0;

    const overlapStart = Math.max(midnightTo8amStart, date1);
    const overlapEnd = Math.min(midnightTo8amEnd, fullTimeEnd);

    if (overlapEnd > overlapStart) {
      overtime = (overlapEnd - overlapStart) / (1000 * 60 * 60);
      overtime = parseFloat(overtime.toFixed(2));
    } else {
      overtime = 0;
    }

    if (overlapEnd <= overlapStart) {
      afterFullTime = true;
    }

    return { timeDiff: diff, afterFullTime, overtime };
  } catch (error) {
    return { timeDiff: 0, afterFullTime: true, overtime: 0 };
  }
}

// NOTE: all these function here for changing the way we show ot and dt
export const getOverTimeEstimate = ({ overTimeEstimate, overT, rate }) => {
  const midnightOTRate = overT * 0.5 * rate;
  const totalOTRate = midnightOTRate + overTimeEstimate;
  return roundToNearestHalf(totalOTRate);
};

export const getFullTimeEstimate = ({ totalHours, rate }) => {
  return roundToNearestHalf(totalHours * rate);
};

/**
 * Feature "Don't charge client" (Complimentary) — see HANDOFF §2/§3.
 *
 * Reads the `clientCharge` object stored on the shift (positionData) by the backend
 * and returns zeroing flags for the CALCULATION OF THE AMOUNT CHARGED TO THE CLIENT.
 * NEVER affects the contractor payment side (PO/SOW).
 *
 * Rules:
 *  - Missing `clientCharge` OR isChargeable !== false ⇒ charge normally.
 *  - isChargeable === false + scope 'full'   ⇒ zero everything (base, OT/DT, meal, travel).
 *  - isChargeable === false + scope 'otOnly' ⇒ zero only OT/DT; base/meal/travel stay.
 *
 * `zeroOt`   = zero out overtime/OT/DT (applies to full AND otOnly).
 * `zeroBase` = zero out base/full-time + meal + travel (applies only to full).
 *
 * IMPORTANT: preserve `appliedRate`/`hours` in the displayed object — only the `estimate`
 * (displayed/summed value) goes to zero, so the internal view shows the real value.
 */
export const getComplimentaryFlags = (pos) => {
  const cc = pos?.clientCharge;
  const chargeable = !(cc && cc.isChargeable === false);
  const scope = cc?.scope === "otOnly" ? "otOnly" : "full";
  return {
    chargeable,
    scope,
    reason: cc?.reason || "",
    isComplimentary: !chargeable && scope === "full",
    isComplimentaryOT: !chargeable && scope === "otOnly",
    zeroOt: !chargeable, // OT/DT are zeroed in full AND otOnly
    zeroBase: !chargeable && scope === "full", // base/meal/travel are zeroed only in full
  };
};

/**
 * Applies the complimentary zeroing to the `estimate` of ONE subSection item,
 * based on the hour-type index. Reused across invoice/estimate/deposit.
 *
 * idx: 0=base, 1=overT, 2=otHours, 3=dtHours, 4=meal, 5=travel
 */
export const applyComplimentaryToSubItem = (estimate, idx, flags) => {
  if (!flags || flags.chargeable) return estimate;
  if (idx === 1 || idx === 2 || idx === 3) {
    return flags.zeroOt ? 0 : estimate;
  }
  // base (0), meal (4), travel (5) — zeroed only in scope 'full'
  return flags.zeroBase ? 0 : estimate;
};

/**
 * function to roundoff values
 * @param {Number} value
 */
export function roundOffValue(value) {
  try {
    if (isNaN(value)) return 0;
    // return Math.round(value);
    return parseFloat(value).toFixed(2);
  } catch (e) {
    return 0;
  }
}

/**
 * function to format number beautifully
 * @params {Number} num
 */
export function formatNumber(num) {
  return Number.isInteger(num) ? num : parseFloat(num.toFixed(2));
}

/**
 * if contractor and client terms for positons are different
 * @param {Object} options
 * @param {Object} options.pos
 * @param {Boolean} options.isCalifornia
 * @returns {boolean}
 */
export function isClientAndConPosTermsDiffrent({ pos, isCalifornia = false }) {
  if (isCalifornia) return true;

  const termsForContractor = isCalifornia
    ? false
    : (pos?.positionTerms?.termsForContractor ?? false);
  return (
    (pos?.positionTerms?.termsForClient ?? false) !== termsForContractor ||
    (pos?.positionTerms?.clientHourlyRate ?? false) !==
      (pos?.positionTerms?.contractorHourlyRate ?? false) ||
    (pos?.positionTerms?.clientHalfDayInc ?? false) !==
      (pos?.positionTerms?.contractorHalfDayInc ?? false)
  );
}

/**
 * contractor actual fields list
 * @param {Object} options
 * @param {Object} options.pos
 *
 * @returns {boolean}
 */
export const isContractorFieldsAvailable = ({ pos }) => {
  [
    "contractorActualStart",
    "contractorActualEnd",
    "contractorFirstBreak",
    "contractorAfterFirstBreakIn",
    "contractorSecondBreak",
    "contractorAfterSecondBreakIn",
    "contractorOT",
    "contractorDT",
    "contractorActualMidnightOT",
    "contractorMealPenalty",
    "contractorMealPenaltyHours",
  ].some((key) => pos?.[key] !== undefined && pos?.[key] !== null);
};

/**
 * is contractor diff for PO
 * @param {Object} options
 * @param {Object} options.pos
 * @param {Boolean} options.isCalifornia
 */
export const isContractorDiffForPO = ({ pos, isCalifornia }) => {
  return (
    // isContractorFieldsAvailable({ pos }) ||
    isClientAndConPosTermsDiffrent({ pos, isCalifornia })
  );
};

/**
 * get california contractor rate
 * @param {Number} val
 *
 * @returns {Number}
 */
export const getCaliforniaContractorRate = (val) => {
  if (!val) return 0.0;
  return parseFloat((val * 10) / 11).toFixed(2);
};

/**
 * Rounds a number to the nearest 0.5 (half-unit) based on custom threshold logic.
 *
 * - Values with a decimal < 0.25 round down to the nearest whole number.
 * - Values with a decimal ≥ 0.25 and < 0.75 round to the nearest 0.5.
 * - Values with a decimal ≥ 0.75 round up to the next whole number.
 *
 * @param {number} num - The input number to be rounded.
 * @returns {number} The number rounded to the nearest 0.5 increment.
 *
 * @example
 * roundToNearestHalf(50.15); // 50.0
 * roundToNearestHalf(50.35); // 50.5
 * roundToNearestHalf(50.75); // 51.0
 * roundToNearestHalf(50.5);  // 50.5
 */
export function roundToNearestHalf(num) {
  return num;
  // const floor = Math.floor(num);
  // const fraction = num - floor;
  //
  // if (fraction < 0.25) {
  //   return floor; // round down to .00
  // } else if (fraction < 0.75) {
  //   return floor + 0.5; // round to .50
  // } else {
  //   return floor + 1; // round up to next integer
  // }
}

/**
 * calcuate travel days pay for either client or contractor or both
 */
export function getTravelDaysPay({
  data,
  isClient,
  fullDay,
  halfDay,
  appliedRate,
}) {
  if (!data?.travelDetails || !Array.isArray(data.travelDetails)) return 0;

  const newData = data.travelDetails.filter((d) => {
    if (isClient) {
      return (
        d?.chargesAppliesTo !== TRAVEL_DAYS.CHARGES_APPLIED_TO?.CONTRACTOR_ONLY
      );
    } else {
      return (
        d?.chargesAppliesTo !== TRAVEL_DAYS.CHARGES_APPLIED_TO?.CUSTOMER_ONLY
      );
    }
  });
  if (!newData || newData.length === 0) return 0;

  if (newData?.[0]?.customRateType == TRAVEL_DAYS.CUSTOM_AMOUNT_TYPE.LUMP_SUM)
    return newData?.[0]?.customRateValue;

  return roundToNearestHalf(
    newData?.reduce((total, item) => {
      if (item.rate === TRAVEL_DAYS.RATE_TYPE.CUSTOM_AMOUNT) {
        return total + (item.customRateValue || 0);
      }

      if (item.rate === TRAVEL_DAYS.RATE_TYPE.FULL_DAY) {
        return total + (fullDay || 0) * appliedRate;
      }

      if (item.rate === TRAVEL_DAYS.RATE_TYPE.HALF_DAY) {
        return total + (halfDay || 0) * appliedRate;
      }

      return total;
    }, 0) || 0,
  );
}

/**
 * function to format phone number from 1234567890 to 123-456-7890
 * @param {string} number
 * @returns {string} string
 */
export function phoneNumberFormat(number) {
  if (!number) return "";
  if (number.match(/^\d{3}-\d{3}-\d{4}$/)) {
    return number;
  }
  const cleaned = `${number}`.replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  return number;
}

/**
 * contractor types
 */
const CONTRACTOR_TYPE_3RD_PARTY = {
  OUTSOURCED: "outSource",
  THIRD_PARTY: "thirdParty",
};

/**
 * check if a contractor is third party
 */
export const isThirdPartyContractor = ({ row }) => {
  return (
    row?.contractorType == CONTRACTOR_TYPE_3RD_PARTY.OUTSOURCED ||
    row?.contractorType == CONTRACTOR_TYPE_3RD_PARTY.THIRD_PARTY
  );
};

/**
 *
 */
export const handleMobileChange = (e) => {
  const input = e.target.value;
  const formattedInput = input
    .replace(/\D/g, "")
    .replace(/(\d{1,3})(\d{1,3})?(\d{1,4})?/, function (_, p1, p2, p3) {
      let formattedNumber = "";
      if (p1) {
        formattedNumber += p1;
      }
      if (p2) {
        formattedNumber += "-" + p2;
      }
      if (p3) {
        formattedNumber += "-" + p3;
      }
      return formattedNumber;
    });
  // handleChange({
  //   target: {
  //     name: "mobile",
  //     value: formattedInput,
  //   },
  // });
};

/**
 * get start and end time if added or get tbd of hours for either estimate and invoice
 */
export const getStartAndEndTime = ({ row, timezone, isEstimate }) => {
  const timeInField = isEstimate ? "timeIn" : "actualStart";
  const timeOutField = isEstimate ? "timeOut" : "actualEnd";
  if (
    isTimeTBD(row?.[timeInField] || row?.timeIn) ||
    isTimeTBD(row?.[timeOutField] || row?.timeOut)
  ) {
    return `TBD (${row?.numberOfHours} ${
      row?.numberOfHours > 1 ? "Hours" : "Hour"
    })`;
  } else {
    const startTime = formatAMPM(
      convertFromUTCToGivenTimezone({
        dateStr: row?.[timeInField] || row?.timeIn,
        timezone: timezone,
      }),
    );
    const endTime = formatAMPM(
      convertFromUTCToGivenTimezone({
        dateStr: row?.[timeOutField] || row?.timeOut,
        timezone: timezone,
      }),
    );
    return `${startTime} - ${endTime}`;
  }
};

/**
 * check if the value is present or not
 */
export const checkValidValue = ({ value }) => {
  if (isNull(value) || isUndefined(value) || value === "") {
    return false;
  } else return true;
};
